import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Mail, Briefcase, LogOut, Search, Download, Trash2, X, Inbox,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Seo } from "../components/Seo";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white border border-gray-100 p-8" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="text-[0.68rem] uppercase tracking-[0.2em] text-darkgray/50 font-semibold">{label}</div>
    <div className="mt-4 font-serif font-light text-5xl text-navy">{value}</div>
    {sub && <div className="mt-2 text-xs text-gold">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [careers, setCareers] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try {
      const [c, k, s] = await Promise.all([
        api.get("/admin/contacts"),
        api.get("/admin/careers"),
        api.get("/admin/stats"),
      ]);
      setContacts(c.data);
      setCareers(k.data);
      setStats(s.data);
    } catch {
      toast.error("Failed to load submissions.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const rows = tab === "contacts" ? contacts : careers;
  const filtered = useMemo(() => {
    const term = q.toLowerCase().trim();
    if (!term) return rows;
    return rows.filter((r) =>
      Object.values(r).some((v) => String(v || "").toLowerCase().includes(term))
    );
  }, [rows, q]);

  const del = async (id) => {
    try {
      await api.delete(`/admin/${tab}/${id}`);
      toast.success("Submission deleted.");
      load();
      setDetail(null);
      setConfirmDel(null);
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/${tab}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tab}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed.");
    }
  };

  return (
    <div className="min-h-screen bg-lightgray flex flex-col md:flex-row" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard | Next Horizon Builders" description="Next Horizon Builders admin dashboard." path="/admin" noindex />
      {/* Sidebar */}
      <aside className="md:w-72 bg-navy text-white md:min-h-screen flex md:flex-col justify-between p-6 md:p-8">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <img src="/media/logo-mark-white.png" alt="NHB" className="h-9 w-auto" />
            <div className="hidden md:block leading-none">
              <div className="font-serif text-lg">Next Horizon</div>
              <div className="text-[0.55rem] tracking-[0.3em] uppercase text-white/60">Admin</div>
            </div>
          </div>
          <nav className="flex md:flex-col gap-2">
            {[
              { id: "contacts", label: "Contact Inquiries", icon: Mail },
              { id: "careers", label: "Career Applications", icon: Briefcase },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => { setTab(t.id); setQ(""); }}
                  data-testid={`admin-tab-${t.id}`}
                  className={`flex items-center gap-3 px-4 py-3.5 text-sm transition-colors duration-300 ${
                    tab === t.id ? "bg-gold text-white" : "text-white/70 hover:bg-white/10"
                  }`}
                >
                  <Icon size={18} />
                  <span className="hidden md:inline">{t.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
        <button
          onClick={doLogout}
          data-testid="admin-logout"
          className="flex items-center gap-3 px-4 py-3.5 text-sm text-white/70 hover:bg-white/10 transition-colors"
        >
          <LogOut size={18} />
          <span className="hidden md:inline">Sign Out</span>
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-12 overflow-x-hidden">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <LayoutDashboard className="text-gold" size={28} />
            <div>
              <h1 className="font-serif font-light text-3xl md:text-4xl text-navy">Dashboard</h1>
              <p className="text-sm text-darkgray/50">Signed in as {user?.email}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <StatCard label="Total Inquiries" value={stats.contacts} sub={`+${stats.recent_contacts} this week`} />
            <StatCard label="Total Applications" value={stats.careers} sub={`+${stats.recent_careers} this week`} />
            <StatCard label="This Week" value={stats.recent_contacts + stats.recent_careers} sub="New submissions" />
            <StatCard label="All Submissions" value={stats.contacts + stats.careers} />
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-white border border-gray-100">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-5 border-b border-gray-100">
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-0 top-1/2 -translate-y-1/2 text-darkgray/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="admin-search"
                placeholder={`Search ${tab}...`}
                className="w-full bg-transparent border-b border-gray-200 pl-7 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors font-light"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-darkgray/50">{filtered.length} of {rows.length}</span>
              <button
                onClick={exportCsv}
                data-testid="admin-export"
                className="inline-flex items-center gap-2 border border-navy text-navy px-5 py-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.15em] hover:bg-navy hover:text-white transition-colors"
              >
                <Download size={15} /> Export CSV
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {filtered.length === 0 ? (
              <div className="py-24 text-center text-darkgray/40" data-testid="admin-empty">
                <Inbox size={40} className="mx-auto mb-4 opacity-40" />
                <p>No submissions found.</p>
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-[0.65rem] uppercase tracking-[0.15em] text-darkgray/50">
                    <th className="px-5 py-4 font-semibold">Name</th>
                    <th className="px-5 py-4 font-semibold hidden md:table-cell">Email</th>
                    <th className="px-5 py-4 font-semibold hidden lg:table-cell">
                      {tab === "contacts" ? "Project Type" : "Position"}
                    </th>
                    <th className="px-5 py-4 font-semibold hidden lg:table-cell">Date</th>
                    <th className="px-5 py-4 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-lightgray/60 transition-colors" data-testid={`admin-row-${r.id}`}>
                      <td className="px-5 py-4 font-medium text-navy">{r.name}</td>
                      <td className="px-5 py-4 hidden md:table-cell text-darkgray/70">{r.email}</td>
                      <td className="px-5 py-4 hidden lg:table-cell text-darkgray/70">
                        {tab === "contacts" ? (r.project_type || "—") : r.position}
                      </td>
                      <td className="px-5 py-4 hidden lg:table-cell text-darkgray/50 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-4">
                          <button onClick={() => setDetail(r)} data-testid={`admin-view-${r.id}`} className="text-navy hover:text-gold text-xs font-semibold uppercase tracking-wider transition-colors">View</button>
                          <button onClick={() => setConfirmDel(r)} data-testid={`admin-delete-${r.id}`} className="text-darkgray/40 hover:text-red-600 transition-colors"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-detail">
          <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={() => setDetail(null)} />
          <div className="relative w-full max-w-md bg-white h-full overflow-y-auto p-8 md:p-10 shadow-2xl">
            <div className="flex items-center justify-between mb-8">
              <span className="eyebrow">{tab === "contacts" ? "Contact Inquiry" : "Application"}</span>
              <button onClick={() => setDetail(null)} className="text-darkgray/50 hover:text-navy"><X size={22} /></button>
            </div>
            <h2 className="font-serif font-light text-4xl text-navy">{detail.name}</h2>
            <p className="text-darkgray/50 text-sm mt-1">{fmtDate(detail.created_at)}</p>
            <div className="mt-8 space-y-5 text-sm">
              {Object.entries({
                Email: detail.email,
                Phone: detail.phone,
                ...(tab === "contacts"
                  ? { Company: detail.company, "Project Type": detail.project_type, Message: detail.message }
                  : { Position: detail.position, Experience: detail.experience, LinkedIn: detail.linkedin, Message: detail.message }),
              }).map(([k, v]) => (
                <div key={k} className="border-b border-gray-100 pb-4">
                  <div className="text-[0.62rem] uppercase tracking-[0.2em] text-darkgray/40 font-semibold mb-1.5">{k}</div>
                  <div className="text-darkgray whitespace-pre-wrap break-words">{v || "—"}</div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setConfirmDel(detail)}
              className="mt-8 inline-flex items-center gap-2 text-red-600 text-xs font-semibold uppercase tracking-wider"
            >
              <Trash2 size={15} /> Delete Submission
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDel && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-6" data-testid="admin-confirm-delete">
          <div className="absolute inset-0 bg-navy/50 backdrop-blur-sm" onClick={() => setConfirmDel(null)} />
          <div className="relative bg-white w-full max-w-sm p-8 shadow-2xl">
            <h3 className="font-serif font-light text-3xl text-navy">Delete submission?</h3>
            <p className="mt-3 text-sm text-darkgray/60 leading-relaxed">
              This will permanently remove <span className="text-navy font-medium">{confirmDel.name}</span>'s
              submission. This action cannot be undone.
            </p>
            <div className="mt-8 flex gap-3">
              <button
                onClick={() => setConfirmDel(null)}
                data-testid="admin-confirm-cancel"
                className="flex-1 border border-gray-300 text-darkgray px-5 py-3 text-[0.68rem] font-semibold uppercase tracking-[0.15em] hover:bg-lightgray transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => del(confirmDel.id)}
                data-testid="admin-confirm-delete-btn"
                className="flex-1 bg-red-600 text-white px-5 py-3 text-[0.68rem] font-semibold uppercase tracking-[0.15em] hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
